import React, { useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  useTheme
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,    
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Release } from '../types';
import ShimmerCard from './ShimmerCard';

interface ReleaseStatisticsProps {
  releases: Release[];
  isLoading?: boolean;
}
const ReleaseStatistics: React.FC<ReleaseStatisticsProps> = ({ releases, isLoading = false }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  const stats = useMemo(() => {
    const statusCount = {
      Past: releases.filter(r => r.status === 'Past').length,
      Current: releases.filter(r => r.status === 'Current').length,
      Planned: releases.filter(r => r.status === 'Planned').length
    };

    const typeCount = {
      Development: releases.filter(r => r.releaseType === 'Development').length,
      Staging: releases.filter(r => r.releaseType === 'Staging').length,
      Production: releases.filter(r => r.releaseType === 'Production').length
    };

    // Monthly release trend
    const monthlyData = releases.reduce((acc, release) => {
      const date = new Date(release.releaseDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      
      if (!acc[monthKey]) {
        acc[monthKey] = {
          month: monthLabel,
          timestamp: date.getTime(),
          count: 0,
          Development: 0,
          Staging: 0,
          Production: 0
        };
      }
      acc[monthKey].count++;
      acc[monthKey][release.releaseType]++;
      return acc;
    }, {} as Record<string, any>);

    const monthlyTrend = Object.values(monthlyData)
      .sort((a: any, b: any) => a.timestamp - b.timestamp)
      .map(({ timestamp, ...rest }) => rest);

    // Status pie data
    const statusPieData = Object.entries(statusCount)
      .filter(([_, count]) => count > 0)
      .map(([status, count]) => ({
        name: status,
        value: count
      }));

    // Type pie data
    const typePieData = Object.entries(typeCount)
      .filter(([_, count]) => count > 0)
      .map(([type, count]) => ({
        name: type,
        value: count
      }));

    // Combined status-type data for radar
    const radarData = [
      {
        category: 'Past',
        count: statusCount.Past,
        fullMark: Math.max(...Object.values(statusCount), 1)
      },
      {
        category: 'Current',
        count: statusCount.Current,
        fullMark: Math.max(...Object.values(statusCount), 1)
      },
      {
        category: 'Planned',
        count: statusCount.Planned,
        fullMark: Math.max(...Object.values(statusCount), 1)
      }
    ];

    return {
      total: releases.length,
      statusCount,
      typeCount,
      statusPieData,
      typePieData,
      monthlyTrend,
      radarData
    };
  }, [releases]);

  const STATUS_COLORS = {
    Past: '#4caf50',
    Current: '#2196f3',
    Planned: '#9e9e9e'
  };

  const TYPE_COLORS = {
    Development: '#ff9800',
    Staging: '#ffc107',
    Production: '#4caf50'
  };

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent
  }: any) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        style={{ fontWeight: 'bold' }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (isLoading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <ShimmerCard variant="statistics" />
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <ShimmerCard variant="statistics" />
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <ShimmerCard variant="statistics" />
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <ShimmerCard variant="statistics" />
          </Box>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <ShimmerCard variant="statistics" />
          <ShimmerCard variant="statistics" />
          <ShimmerCard variant="statistics" />
          <ShimmerCard variant="statistics" />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Releases
              </Typography>
              <Typography variant="h3">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card sx={{ bgcolor: isDark ? 'rgba(76, 175, 80, 0.15)' : '#e8f5e9' }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Past Releases
              </Typography>
              <Typography variant="h3">
                {stats.statusCount.Past}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card sx={{ bgcolor: isDark ? 'rgba(33, 150, 243, 0.15)' : '#e3f2fd' }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Current Releases
              </Typography>
              <Typography variant="h3">
                {stats.statusCount.Current}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card sx={{ bgcolor: isDark ? 'rgba(158, 158, 158, 0.15)' : '#f5f5f5' }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Planned Releases
              </Typography>
              <Typography variant="h3">
                {stats.statusCount.Planned}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Status Distribution */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 300px', minWidth: { xs: '100%', sm: '300px', md: '400px' } }}>
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom>
              Status Distribution
            </Typography>
            {stats.statusPieData.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Typography variant="body1" color="text.secondary">
                  No data to display
                </Typography>
              </Box>
            ) : (
              <Box sx={{ width: '100%', height: { xs: 250, sm: 300 } }}>
                <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats.statusPieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {stats.typePieData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={TYPE_COLORS[entry.name as keyof typeof TYPE_COLORS]} 
                      />
                    ))}
                  </Pie>
                  <Legend wrapperStyle={{ color: theme.palette.text.primary }} />
                </PieChart>
              </ResponsiveContainer>
              </Box>
            )}
          </Paper>
          </Box>

          {/* Environment Distribution */}
          <Box sx={{ flex: '1 1 300px', minWidth: { xs: '100%', sm: '300px', md: '400px' } }}>
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" gutterBottom>
                Environment Distribution
              </Typography>
            {stats.typePieData.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Typography variant="body1" color="text.secondary">
                  No data to display
                </Typography>
              </Box>
            ) : (
              <Box sx={{ width: '100%', height: { xs: 250, sm: 300 } }}>
                <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats.typePieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {stats.typePieData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={TYPE_COLORS[entry.name as keyof typeof TYPE_COLORS]} 
                      />
                    ))}
                  </Pie>
                  <Legend wrapperStyle={{ color: theme.palette.text.primary }} />
                </PieChart>
              </ResponsiveContainer>
              </Box>
            )}
            </Paper>
          </Box>
        </Box>

        {/* Monthly Trend */}
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" gutterBottom>
            Monthly Release Trend
          </Typography>
            {stats.monthlyTrend.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Typography variant="body1" color="text.secondary">
                  No data to display
                </Typography>
              </Box>
            ) : (
              <Box sx={{ width: '100%', height: { xs: 250, sm: 300 } }}>
                <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.monthlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#444' : '#ccc'} />
                  <XAxis dataKey="month" stroke={theme.palette.text.secondary} />
                  <YAxis stroke={theme.palette.text.secondary} />
                  <Tooltip contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }} />
                  <Legend />
                  <Bar dataKey="Development" stackId="a" fill={TYPE_COLORS.Development} />
                  <Bar dataKey="Staging" stackId="a" fill={TYPE_COLORS.Staging} />
                  <Bar dataKey="Production" stackId="a" fill={TYPE_COLORS.Production} />
                </BarChart>
              </ResponsiveContainer>
              </Box>
            )}
        </Paper>

        {/* Status Overview and Type Bar Chart */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 300px', minWidth: { xs: '100%', sm: '300px', md: '400px' } }}>
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" gutterBottom>
                Status Overview
              </Typography>
              {releases.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <Typography variant="body1" color="text.secondary">
                    No data to display
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ width: '100%', height: { xs: 250, sm: 300 } }}>
                  <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.statusPieData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#444' : '#ccc'} />
                    <XAxis dataKey="name" stroke={theme.palette.text.secondary} />
                    <YAxis stroke={theme.palette.text.secondary} />
                    <Tooltip contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }} />
                    <Bar dataKey="value" fill="#8884d8">
                      {stats.statusPieData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={STATUS_COLORS[entry.name as keyof typeof STATUS_COLORS]} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                </Box>
              )}
            </Paper>
          </Box>          {/* Type Bar Chart */}
          <Box sx={{ flex: '1 1 300px', minWidth: { xs: '100%', sm: '300px', md: '400px' } }}>
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" gutterBottom>
                Releases by Environment
              </Typography>
              {stats.typePieData.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <Typography variant="body1" color="text.secondary">
                    No data to display
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ width: '100%', height: { xs: 250, sm: 300 } }}>
                  <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.typePieData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#444' : '#ccc'} />
                    <XAxis dataKey="name" stroke={theme.palette.text.secondary} />
                    <YAxis stroke={theme.palette.text.secondary} />
                    <Tooltip contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }} />
                    <Bar dataKey="value" fill="#8884d8">
                      {stats.typePieData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={TYPE_COLORS[entry.name as keyof typeof TYPE_COLORS]} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ReleaseStatistics;
